import React, { useState, useCallback, useEffect, useRef } from 'react';
import { AppShell, Button } from '@cloudsam/ui';
import { ReactFlowProvider } from 'reactflow';
import GraphEditor from './components/GraphEditor/GraphEditor';
import PredicateEditor from './components/PredicateEditor/PredicateEditor';
import Toolbar from './components/Toolbar/Toolbar';
import SamLogo from './components/SamLogo';
import { openHelpAgent, notifyHelpAgent } from './utils/openHelpAgent';
import { HelpCircle } from 'lucide-react';
import { useAuth } from './context/AuthContext';
import { NotificationProvider } from './components/Notifications/NotificationContext';
import NotificationContainer from './components/Notifications/NotificationContainer';
import { validateSurveyFlow, validateResponseCoverage } from './utils/responseValidation';
import './App.css';
import VariablesPanel from './components/VariablesPanel';
import FloatingPanel from './components/common/FloatingPanel';
import TabbedNodeEditor from './components/PredicateEditor/NodeEditor';
import SurveySettings from './components/PredicateEditor/SurveySettings';
import ValidationPanel from './components/PredicateEditor/ValidationPanel';
import EdgeEditor from './components/PredicateEditor/EdgeEditor';
import { buildVariableMap } from './utils/variableScanner';
import { sanitizeLegacyExpression } from './utils/conditionGenerator';
import { applySelectedAutoFixes, planAutoFixesDetailed } from './utils/autoFixers';
import { buildNormalizedGraph, buildFlowFromGraph } from './utils/graphNormalize';
import { preparePayload } from './utils/assistantBridge';
import AssistantPanel from './components/AssistantPanel';
import { DesignerDerivedProvider } from './components/DesignerDerivedContext';
import { layoutGraph } from './utils/layoutGraph';
import { normalizeCanvasSettings, ptToPx } from './constants/canvasSettings';
import { fetchUserPrefsFromCloud, saveUserPrefsToCloud } from './utils/cloudApi';
import CanvasAppearanceModal from './components/GraphEditor/CanvasAppearanceModal';
import AutoFixModal from './components/AutoFixModal';
import { useDraftSaver, useContextLabel } from './context/IdleContext';
import { draftStore, DRAFT_SCOPE, NEW_SURVEY_DRAFT_KEY, autoSaveNotes } from './idle-session';
import DraftRecoveryNotice from './components/DraftRecoveryNotice';

function App({ initialPayload, initialSurveyContext }) {
  const { user } = useAuth();
  // selectedNode/selectedEdge stay singular for the editor panels, which only
  // ever edit one thing. selectedNodes/selectedEdges carry the full selection
  // (shift-click, shift-drag box) for move and delete.
  const [selectedNode, setSelectedNode] = useState(null);
  const [selectedEdge, setSelectedEdge] = useState(null);
  const [selectedNodes, setSelectedNodes] = useState([]);
  const [selectedEdges, setSelectedEdges] = useState([]);
  const [rightPanelWidth, setRightPanelWidth] = useState(400);
  const [leftPanelWidth, setLeftPanelWidth] = useState(400);

  // Auto-track: tell the help popup we're in Designer whenever this tab gets focus
  useEffect(() => {
    const notify = () => notifyHelpAgent('designer', user?.user_id);
    window.addEventListener('focus', notify);
    return () => window.removeEventListener('focus', notify);
  }, [user?.user_id]);

  const [isLeftDragging, setIsLeftDragging] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  // Normalize nodes to unify legacy data.question_text → data.text
  const normalizeNodes = (arr) => {
    if (!Array.isArray(arr)) return [];
    return arr.map((n) => {
      const data = n.data || {};
      const { question_text, ...rest } = data;
      const textValue = (data.text !== undefined && data.text !== null) ? data.text : (question_text || '');
      return { ...n, data: { ...rest, text: textValue } };
    });
  };
  const [surveyData, setSurveyData] = useState((initialPayload && initialPayload.survey) || {
    title: 'New Survey',
    description: '',
    estimatedDuration: '',
    author: '',
    version: '1.0'
  });
  const [nodes, setNodes] = useState(normalizeNodes((initialPayload && Array.isArray(initialPayload.nodes) && initialPayload.nodes) || []));
  const [edges, setEdges] = useState((initialPayload && Array.isArray(initialPayload.edges) && initialPayload.edges) || []);
  const [variableMap, setVariableMap] = useState(new Map());
  const [wfGuard, setWfGuard] = useState({ show: false, errors: 0, warnings: 0 });
  const [isDirty, setIsDirty] = useState(false);
  const [currentSurveyId, setCurrentSurveyId] = useState(initialSurveyContext?.surveyId || null);
  const [currentVersionNumber, setCurrentVersionNumber] = useState(initialSurveyContext?.versionNumber || null);
  const [edgeValidation, setEdgeValidation] = useState(null);
  const [fullValidationResults, setFullValidationResults] = useState(null);
  const [showAssistant, setShowAssistant] = useState(false);
  const [, setUndoStack] = useState([]); // undo stack managed via setter only
  // Canvas appearance (background, node shading, default connector style,
  // canvas font sizes). This is a USER preference — stored per user via
  // sam-dbam's user-prefs endpoint (GCS-backed), applied to every survey the
  // user opens. It is no longer read from or written to the survey payload.
  const [canvasSettings, setCanvasSettings] = useState(() => normalizeCanvasSettings(null));
  const userPrefsRef = useRef({});
  const prefsSaveTimer = useRef(null);

  // Load the user's stored preferences once at startup.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const prefs = await fetchUserPrefsFromCloud();
        if (cancelled) return;
        userPrefsRef.current = (prefs && typeof prefs === 'object') ? prefs : {};
        setCanvasSettings(normalizeCanvasSettings(userPrefsRef.current?.designer?.canvas));
      } catch (e) {
        // Prefs are a nicety; fall back to defaults without complaint.
        console.warn('[prefs] user prefs unavailable, using defaults:', e?.message || e);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Merge the current canvas settings into the user's prefs object and save
  // (debounced — swatch clicking shouldn't hammer the endpoint).
  const persistCanvasPrefs = useCallback((settings) => {
    userPrefsRef.current = {
      ...userPrefsRef.current,
      designer: { ...(userPrefsRef.current.designer || {}), canvas: settings },
    };
    if (prefsSaveTimer.current) clearTimeout(prefsSaveTimer.current);
    prefsSaveTimer.current = setTimeout(() => {
      saveUserPrefsToCloud(userPrefsRef.current)
        .catch(e => console.warn('[prefs] save failed:', e?.message || e));
    }, 600);
  }, []);
  const [showAppearanceModal, setShowAppearanceModal] = useState(false);
  // Auto-fix picker state: null = closed, otherwise { items }
  const [autoFixPlan, setAutoFixPlan] = useState(null);
  const errorNodeIds = React.useMemo(() => new Set(), []);
  const warningNodeIds = React.useMemo(() => new Set(), []);
  const issueByNode = React.useMemo(() => new Map(), []);

  // --- Tear-off floating panel state ---
  const [undockedPanels, setUndockedPanels] = useState(() => {
    try {
      const saved = localStorage.getItem('designer-undocked-panels');
      return saved ? new Set(JSON.parse(saved)) : new Set();
    } catch (_) { return new Set(); }
  });

  const onUndock = useCallback((sectionId) => {
    setUndockedPanels(prev => {
      const next = new Set(prev);
      next.add(sectionId);
      try { localStorage.setItem('designer-undocked-panels', JSON.stringify([...next])); } catch (_) { }
      return next;
    });
  }, []);

  const onDock = useCallback((sectionId) => {
    setUndockedPanels(prev => {
      const next = new Set(prev);
      next.delete(sectionId);
      try { localStorage.setItem('designer-undocked-panels', JSON.stringify([...next])); } catch (_) { }
      return next;
    });
  }, []);

  // Expose for URL change detection by Console
  useEffect(() => {
    window.__designerIsDirty = () => isDirty;
    return () => { delete window.__designerIsDirty; };
  }, [isDirty]);

  // ── Rescuing unsaved work before an idle sign-out ───────────────────────
  //
  // The Toolbar owns the payload builder and the compile-and-save path, and
  // hands both up through this ref. See Toolbar's onExposeSaveApi.
  const saveApiRef = useRef(null);
  const exposeSaveApi = useCallback((api) => { saveApiRef.current = api; }, []);

  const userId = user?.user_id || '';

  // Runs at the 28-minute warning, two minutes before the lock. That gap is the
  // point: it gives a slow cloud save time to finish, and it covers a browser
  // crash in the last two minutes too.
  //
  // Two saves, because they fail in different ways. The local draft is instant,
  // needs no network and cannot be refused by an expired token — it is the one
  // that always holds. The cloud draft survives the machine dying, and may
  // fail. Local first, always.
  useDraftSaver(async () => {
    // Nothing unsaved is different from "tried and failed". Returning nothing
    // keeps the lock screen from claiming a save that never happened.
    if (!isDirty) return undefined;
    const api = saveApiRef.current;
    if (!api || !userId) return undefined;

    const label = surveyData?.title || 'Untitled survey';
    let payload = null;
    try {
      payload = api.buildPayload();
    } catch (err) {
      return { saved: false, where: 'local', error: 'the survey could not be read' };
    }

    const localSaved = draftStore.saveDraft({
      userId,
      scope: DRAFT_SCOPE,
      key: currentSurveyId || NEW_SURVEY_DRAFT_KEY,
      payload,
      basedOnVersion: currentVersionNumber,
      label,
    });

    // A survey that has never been saved has no id to write a version against.
    // Creating one here would leave a survey nobody named, and nobody asked
    // for, sitting in the account. The local draft covers it instead.
    if (!currentSurveyId) {
      return { saved: localSaved, where: 'local', label };
    }

    // Never an overwrite. An author halfway through pulling a survey apart
    // would otherwise have their last good version replaced by the broken one,
    // by the very feature meant to protect them. 'version' only ever adds.
    let result = null;
    try {
      result = await api.saveToCloud({ mode: 'version', notes: autoSaveNotes() });
    } catch (err) {
      result = { ok: false, error: err.message };
    }
    if (result && result.ok) return { saved: true, where: 'cloud', label };
    return { saved: localSaved, where: 'local', label };
  }, [isDirty, userId, currentSurveyId, currentVersionNumber, surveyData]);

  // Named for the lock screen, kept current independent of the draft saver
  // above: that one only fires when there is unsaved work, but a person can
  // lock with nothing dirty and still want to know which survey this tab was.
  useContextLabel(surveyData?.title || 'Untitled survey');

  // ── Offering that work back ─────────────────────────────────────────────
  //
  // Read once, as soon as we know who is signed in. Reading later would race
  // with the author's own edits, and reading repeatedly would re-offer a draft
  // they already discarded.
  const [recoveredDraft, setRecoveredDraft] = useState(null);
  const draftReadRef = useRef(false);

  const draftKeyForSurvey = currentSurveyId || NEW_SURVEY_DRAFT_KEY;

  useEffect(() => {
    if (!userId || draftReadRef.current) return;
    draftReadRef.current = true;
    const found = draftStore.readDraft({ userId, scope: DRAFT_SCOPE, key: draftKeyForSurvey });
    if (found && found.payload) setRecoveredDraft(found);
  }, [userId, draftKeyForSurvey]);

  // Never restore on its own. Somebody who deliberately abandoned a bad edit
  // would be handed it straight back with no idea where it came from.
  const restoreDraft = useCallback(() => {
    const draft = recoveredDraft;
    if (!draft || !draft.payload) return;
    setSurveyData(draft.payload.survey || surveyData);
    setNodes(normalizeNodes(Array.isArray(draft.payload.nodes) ? draft.payload.nodes : []));
    setEdges(Array.isArray(draft.payload.edges) ? draft.payload.edges : []);
    setSelectedNode(null);
    setSelectedEdge(null);
    setFullValidationResults(null);
    setEdgeValidation(null);
    // Restored work is unsaved work. Marking it clean would let the author
    // close the tab believing it had been written somewhere. It has not.
    setIsDirty(true);
    draftStore.clearDraft({ userId, scope: DRAFT_SCOPE, key: draftKeyForSurvey });
    setRecoveredDraft(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recoveredDraft, userId, draftKeyForSurvey]);

  const discardDraft = useCallback(() => {
    draftStore.clearDraft({ userId, scope: DRAFT_SCOPE, key: draftKeyForSurvey });
    setRecoveredDraft(null);
  }, [userId, draftKeyForSurvey]);

  // A save that landed means the draft has done its job. Without this the
  // author would be offered stale work every time they reopened the survey.
  const handleCloudSaved = useCallback(() => {
    if (!userId) return;
    draftStore.clearDraft({ userId, scope: DRAFT_SCOPE, key: draftKeyForSurvey });
    draftStore.clearDraft({ userId, scope: DRAFT_SCOPE, key: NEW_SURVEY_DRAFT_KEY });
    setRecoveredDraft(null);
  }, [userId, draftKeyForSurvey]);

  // ── Survey Assistant bridge ──
  // Allows the AI to inject .cloudsam JSON directly into designer state.
  // Entry points:
  //   1. window.__designerLoadSurvey(descriptorJson)   — inject .cloudsam into canvas
  //   2. window.__designerExportSurvey()                — snapshot canvas as .cloudsam
  //   3. CustomEvent 'assistant-load-survey'            — same-window event
  //   4. postMessage { type: 'assistant-load-survey' }  — cross-window (Console)
  useEffect(() => {
    const loadFromDescriptor = (descriptor) => {
      const { success, payload, error } = preparePayload(descriptor);
      if (!success) {
        console.error('[Assistant Bridge] Load failed:', error);
        return { success: false, error };
      }
      // Undo snapshot — save current state so the author can revert
      setUndoStack(prev => [...prev, {
        nodes: JSON.parse(JSON.stringify(nodes)),
        edges: JSON.parse(JSON.stringify(edges)),
        survey: JSON.parse(JSON.stringify(surveyData)),
      }]);
      setSurveyData(payload.survey);
      setNodes(normalizeNodes(payload.nodes));
      setEdges(payload.edges);
      setSelectedNode(null);
      setSelectedEdge(null);
      setFullValidationResults(null);
      setEdgeValidation(null);
      setIsDirty(true);
      console.log('[Assistant Bridge] Survey loaded:', payload.survey.title, `(${payload.nodes.length} nodes, ${payload.edges.length} edges)`);
      return { success: true };
    };

    // Export current canvas as .cloudsam JSON for the assistant to work with
    const exportCurrentSurvey = () => {
      if (!nodes || nodes.length === 0) return null;
      try {
        const questions = nodes
          .filter(n => ['question', 'start', 'end', 'decision', 'media', 'pleasant'].includes(n.type))
          .map(n => {
            const d = n.data || {};
            const qType = n.type === 'start' ? 'introduction'
              : n.type === 'end' ? 'completion'
                : n.type === 'decision' ? 'decision'
                  : n.type === 'pleasant' ? 'pleasantry'
                    : (d.type || d.question_type || 'text');
            const collectsInput = !['introduction', 'completion', 'decision', 'media', 'pleasantry'].includes(qType);
            const entry = {
              question_id: n.id,
              display_id: d.id || n.id,
              question_text: d.text || d.question_text || '',
              question_type: qType,
              collectsInput,
            };
            if (collectsInput && (d.variable || d.variable_name)) {
              entry.variable_name = d.variable || d.variable_name;
            }
            if (d.required !== undefined) entry.required = d.required;
            if (d.choices && Array.isArray(d.choices) && d.choices.length > 0) entry.choices = d.choices;
            if (d.minValue !== undefined) entry.min_value = d.minValue;
            if (d.maxValue !== undefined) entry.max_value = d.maxValue;
            if (d.context) entry.context = d.context;
            if (d.category) entry.category = d.category;
            return entry;
          });
        const flow = edges.map(e => ({
          from: e.source,
          to: e.target,
          condition: e.data?.condition || '',
          priority: e.data?.priority || 0,
        }));
        return {
          survey_descriptor: {
            survey_name: surveyData.title || '',
            survey_purpose: surveyData.description || '',
            estimated_time: surveyData.estimatedDuration || '',
            survey_version: surveyData.version || '1.0',
            language: 'en',
          },
          questions,
          flow,
        };
      } catch (err) {
        console.error('[Assistant Bridge] Export failed:', err);
        return null;
      }
    };

    // Direct function calls
    window.__designerLoadSurvey = loadFromDescriptor;
    window.__designerExportSurvey = exportCurrentSurvey;

    // Same-window custom event
    const onCustomEvent = (e) => loadFromDescriptor(e.detail);
    window.addEventListener('assistant-load-survey', onCustomEvent);

    // Cross-window postMessage (from Console or iframe)
    const onMessage = (e) => {
      if (e.data?.type === 'assistant-load-survey' && e.data?.payload) {
        loadFromDescriptor(e.data.payload);
      }
    };
    window.addEventListener('message', onMessage);

    return () => {
      delete window.__designerLoadSurvey;
      delete window.__designerExportSurvey;
      window.removeEventListener('assistant-load-survey', onCustomEvent);
      window.removeEventListener('message', onMessage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodes, edges, surveyData]);

  // Decorate nodes with wfBadge for START/End and dashed halo for E008 unreachable
  const decoratedNodes = React.useMemo(() => nodes, [nodes]);

  // Decorate edges with issue level/code for edge badges/styling
  const decoratedEdges = React.useMemo(() => edges, [edges]);

  // Handle ReactFlow's native selection changes.
  //
  // Selection flags on the nodes themselves are owned by React Flow: they arrive
  // as 'select' changes through onNodesChange and are applied by applyNodeChanges
  // in GraphEditor. We must NOT recompute them from selectedNode here — doing so
  // collapsed every multi-selection back to a single node (and broke group drag,
  // since React Flow only drags nodes it still considers selected).
  const handleSelectionChange = useCallback(({ selectedNodes, selectedEdges, selectedNode, selectedEdge }) => {
    const selNodes = selectedNodes || (selectedNode ? [selectedNode] : []);
    const selEdges = selectedEdges || (selectedEdge ? [selectedEdge] : []);
    console.log('[App] selection→', { nodes: selNodes.length, edges: selEdges.length });
    setSelectedNodes(selNodes);
    setSelectedEdges(selEdges);
    // Panels edit one thing at a time: a group selection means "nothing to edit".
    setSelectedNode(selNodes.length === 1 ? selNodes[0] : null);
    setSelectedEdge(selEdges.length === 1 ? selEdges[0] : null);

    // Empty selection (pane click) is the one case we clear flags for, so a
    // deselect can't be missed. Non-empty selections are left to React Flow.
    if (!selNodes.length && !selEdges.length) {
      setNodes(prevNodes =>
        prevNodes.some(n => n.selected)
          ? prevNodes.map(node => (node.selected ? { ...node, selected: false } : node))
          : prevNodes
      );
      setEdges(prevEdges =>
        prevEdges.some(e => e.selected)
          ? prevEdges.map(edge => (edge.selected ? { ...edge, selected: false } : edge))
          : prevEdges
      );
    }

    // Run edge validation if exactly one edge is selected
    const soleEdge = selEdges.length === 1 ? selEdges[0] : null;
    if (soleEdge && soleEdge.source) {
      const sourceNode = nodes.find(n => n.id === soleEdge.source);
      if (sourceNode && sourceNode.type === 'question') {
        const outgoingEdges = edges.filter(e => e.source === soleEdge.source);
        const validation = validateResponseCoverage(sourceNode, outgoingEdges);
        setEdgeValidation({ nodeId: soleEdge.source, validation });
      } else {
        setEdgeValidation(null);
      }
    } else {
      setEdgeValidation(null);
    }
  }, [nodes, edges, setNodes, setEdges]);



  const handleSurveyDataChange = useCallback((newData) => {
    setIsDirty(true);
    setSurveyData(prev => ({ ...prev, ...newData }));
  }, []);

  const handleFullValidation = useCallback(() => {
    // advisory only — never blocks save
    const results = validateSurveyFlow(nodes, edges);
    setFullValidationResults(results);
    return results;
  }, [nodes, edges]);

  // Open the auto-fix picker with one row per proposed fix.
  const handleAutoFix = useCallback(() => {
    try {
      const plan = planAutoFixesDetailed({ nodes, edges });
      setAutoFixPlan({ items: plan.items });
    } catch (err) {
      console.error('Auto-fix planning failed', err);
    }
  }, [nodes, edges]);

  // Apply only the fixes the author checked in the picker.
  const handleApplyAutoFixes = useCallback((keys) => {
    try {
      const { nodes: n2, edges: e2, changes } = applySelectedAutoFixes({ nodes, edges }, keys);
      if (changes > 0) {
        // push snapshot for undo
        setUndoStack(prev => [...prev, { nodes: JSON.parse(JSON.stringify(nodes)), edges: JSON.parse(JSON.stringify(edges)) }]);
        setIsDirty(true);
        setNodes(n2);
        setEdges(e2);
        // Re-run validation to refresh panel — advisory only, never blocks save
        const results = validateSurveyFlow(n2, e2);
        setFullValidationResults(results);
      }
    } catch (err) {
      console.error('Auto-fix failed', err);
    } finally {
      setAutoFixPlan(null);
    }
  }, [nodes, edges]);

  // Canvas appearance: background, node shading, default connector style,
  // font sizes. User preference — saved to user prefs, not the survey, so
  // changes don't dirty the survey.
  const handleCanvasSettingsChange = useCallback((patch) => {
    setCanvasSettings(prev => {
      const next = { ...prev, ...patch };
      persistCanvasPrefs(next);
      return next;
    });
  }, [persistCanvasPrefs]);

  // Restyle every connector, including ones with a per-edge override.
  // The edge restyle IS survey data (dirty); the new default style is a pref.
  const handleApplyConnectorStyleToAll = useCallback((styleId) => {
    setUndoStack(prev => [...prev, { nodes: JSON.parse(JSON.stringify(nodes)), edges: JSON.parse(JSON.stringify(edges)) }]);
    setEdges(edges.map(e => ({ ...e, type: styleId, data: { ...(e.data || {}), edgeType: styleId } })));
    setCanvasSettings(prev => {
      const next = { ...prev, defaultEdgeType: styleId };
      persistCanvasPrefs(next);
      return next;
    });
    setIsDirty(true);
  }, [nodes, edges, persistCanvasPrefs]);

  // Clean up layout: arrange nodes by flow depth, top to bottom. Undoable.
  const handleCleanupLayout = useCallback(() => {
    if (!nodes.length) return;
    setUndoStack(prev => [...prev, { nodes: JSON.parse(JSON.stringify(nodes)), edges: JSON.parse(JSON.stringify(edges)) }]);
    setNodes(layoutGraph(nodes, edges, { direction: 'TB' }));
    setIsDirty(true);
    try { window.dispatchEvent(new CustomEvent('graph-fit-view')); } catch (_) { }
  }, [nodes, edges]);

  const handleUndoAutoFix = useCallback(() => {
    setIsDirty(true);
    setUndoStack(prev => {
      if (!prev.length) return prev;
      const last = prev[prev.length - 1];
      setNodes(last.nodes);
      setEdges(last.edges);
      // Re-run validation on undo — advisory only, never blocks save
      const results = validateSurveyFlow(last.nodes, last.edges);
      setFullValidationResults(results);
      const graph = buildNormalizedGraph(last.nodes, last.edges);
      const flowNormalized = buildFlowFromGraph({ edgesClean: graph.edgesClean, startNodeId: graph.startNodeId });
      const nodeById = new Map(last.nodes.map(n => [n.id, n]));
      const allNodeIds = new Set(last.nodes.map(n => n.id));
      const startId = graph.startNodeId;

      let flowCanonical = flowNormalized
        .filter(e => (e.from === 'START' || allNodeIds.has(e.from)) && (allNodeIds.has(e.to) || /^end[_-]/i.test(String(e.to || ''))))
        .map(e => {
          const srcId = (e.from === 'START') ? startId : e.from;
          const srcNode = nodeById.get(srcId);
          const varName = srcNode && srcNode.data && (srcNode.data.variable || srcNode.data.variable_name || srcNode.id);
          const qType = (srcNode && (srcNode.data?.type || srcNode.data?.question_type)) || '';
          let cond = String(e.condition || '').trim();
          if (/^unconditional$/i.test(cond)) cond = '';
          if (String(qType).toLowerCase() === 'yes_no' && varName) {
            if (/==\s*true\b/i.test(cond)) cond = `${varName} == "yes"`;
            else if (/==\s*false\b/i.test(cond)) cond = `${varName} == "no"`;
            else if (/^(yes|if\s+yes)$/i.test(cond)) cond = `${varName} == "yes"`;
            else if (/^(no|if\s+no)$/i.test(cond)) cond = `${varName} == "no"`;
          } else if (cond) {
            cond = sanitizeLegacyExpression(cond);
          }
          return { from: e.from, to: e.to, condition: cond, priority: e.priority };
        });

      try {
        const startNode = nodeById.get(startId);
        const startText = startNode && (startNode.data?.text || startNode.data?.question_text || '');
        if (startText && String(startText).trim().length > 0) {
          const startChildren = flowCanonical.filter(e => e.from === 'START');
          const others = flowCanonical.filter(e => e.from !== 'START');
          const startEdge = { from: 'START', to: startId, condition: '', priority: 1 };
          const remapped = startChildren.map(e => ({ ...e, from: startId }));
          flowCanonical = [startEdge, ...remapped, ...others];
        }
      } catch (_) { }

      const payload = {
        survey: { title: surveyData.title || '' },
        questions: last.nodes.filter(n => ['question', 'start', 'end'].includes(n.type)).map(n => ({
          question_id: n.id,
          question_text: n.data?.text || '',
          question_type: n.type === 'start' ? 'introduction' : (n.type === 'end' ? 'completion' : (n.data?.type || 'text')),
          variable_name: n.data?.variable || n.data?.variable_name || null,
          required: n.data?.required !== false,
          category: n.data?.category || 'general',
          display_id: n.data?.id || n.id
        })),
        flow: flowCanonical
      };
      // well-formed validator removed
      return prev.slice(0, -1);
    });
  }, [surveyData.title]);

  // Fields that are exclusive to specific question types.
  // When the type changes, these are cleared from the old type so they
  // don't linger and cause stale display on the canvas node card.
  const TYPE_EXCLUSIVE_FIELDS = {
    choice: ['choices'],
    number: ['minValue', 'maxValue'],
    scale: ['minValue', 'maxValue'],
    file: ['accept', 'maxSizeMB', 'storage', 'quarantine'],
  };

  const handleNodeUpdate = useCallback((nodeId, updates) => {
    setIsDirty(true);
    setNodes(prevNodes =>
      prevNodes.map(node => {
        if (node.id !== nodeId) return node;

        let mergedData = { ...node.data, ...updates };

        // When type changes, remove fields exclusive to the OLD type
        if (updates.type !== undefined && updates.type !== node.data.type) {
          const fieldsToRemove = TYPE_EXCLUSIVE_FIELDS[node.data.type] || [];
          fieldsToRemove.forEach(f => { delete mergedData[f]; });
        }

        return { ...node, data: mergedData };
      })
    );

    // If variable name changed, update edge conditions referring to it
    if (updates.variable !== undefined) {
      const oldVar = selectedNode?.data?.variable;
      const newVar = updates.variable;
      if (oldVar && newVar && oldVar !== newVar) {
        setEdges((prevEdges) => prevEdges.map((edge) => {
          if (edge.source === nodeId) {
            const cond = edge.data?.condition || '';
            // Replace variable name (with optional trailing ?) wherever it appears as a standalone token
            const pattern = new RegExp(`\\b${oldVar}\\??\\b`, 'g');
            const updatedCond = cond.replace(pattern, newVar);
            return {
              ...edge,
              data: { ...edge.data, condition: updatedCond }
            };
          }
          return edge;
        }));
      }
    }

    // Update selectedNode if it's the one being edited
    if (selectedNode && selectedNode.id === nodeId) {
      setSelectedNode(prev => ({
        ...prev,
        data: { ...prev.data, ...updates }
      }));
    }
  }, [selectedNode]);

  const handleEdgeUpdate = useCallback((edgeId, updates) => {
    setIsDirty(true);
    setEdges(prevEdges =>
      prevEdges.map(edge => {
        if (edge.id !== edgeId) return edge;
        // Handle single-field data update (from EdgeEditor.handleDataChange)
        // _dataFieldUpdate contains only the changed field, merged into the
        // current (fresh) edge.data to avoid stale closure overwrites
        if (updates._dataFieldUpdate) {
          const { _dataFieldUpdate, ...rest } = updates;
          return { ...edge, ...rest, data: { ...(edge.data || {}), ..._dataFieldUpdate } };
        }
        // Full data replacement — still deep-merge to be safe
        if (updates.data) {
          const { data, ...rest } = updates;
          return { ...edge, ...rest, data: { ...(edge.data || {}), ...data } };
        }
        return { ...edge, ...updates };
      })
    );

    if (selectedEdge && selectedEdge.id === edgeId) {
      setSelectedEdge(prev => {
        if (updates._dataFieldUpdate) {
          const { _dataFieldUpdate, ...rest } = updates;
          return { ...prev, ...rest, data: { ...(prev.data || {}), ..._dataFieldUpdate } };
        }
        if (updates.data) {
          const { data, ...rest } = updates;
          return { ...prev, ...rest, data: { ...(prev.data || {}), ...data } };
        }
        return { ...prev, ...updates };
      });
    }
  }, [selectedEdge]);

  const deleteEdgeById = useCallback((edgeId) => {
    setEdges((eds) => eds.filter((e) => e.id !== edgeId));
    if (selectedEdge && selectedEdge.id === edgeId) {
      setSelectedEdge(null);
    }
  }, [selectedEdge]);

  // Handle node highlighting for Sam test visualization
  const DEFAULT_BORDER = '1px solid #ddd';

  const handleHighlightNode = (nodeId) => {
    setNodes(nds => nds.map(node => {
      const isActive = nodeId && node.id === nodeId;
      return {
        ...node,
        style: {
          ...node.style,
          // Use outline so size doesn’t change
          outline: isActive ? '3px solid #667eea' : 'none',
          outlineOffset: isActive ? '2px' : '0px',
          boxShadow: isActive ? '0 0 12px rgba(102,126,234,0.8)' : 'none',
          border: DEFAULT_BORDER,
          // No scale animation – prevents visual jump
          animation: 'none'
        }
      };
    }));
  };

  // Panel splitter drag functionality
  // Build variable map whenever graph changes
  useEffect(() => {
    setVariableMap(buildVariableMap(nodes, edges));
  }, [nodes, edges]);

  // Handle URL parameter changes (when tab is reused by Console)
  useEffect(() => {
    const checkUrlChange = () => {
      const params = new URLSearchParams(window.location.search);
      const newId = params.get('surveyId');

      if (newId && newId !== String(currentSurveyId)) {
        if (isDirty) {
          if (!window.confirm('Discard unsaved changes?')) {
            // Restore old URL to match current state
            const oldUrl = new URL(window.location.href);
            oldUrl.searchParams.set('surveyId', currentSurveyId);
            window.history.pushState(null, '', oldUrl.toString());
            return;
          }
        }
        // Force reload to pick up the new surveyId from URL
        window.location.reload();
      }
    };

    window.addEventListener('focus', checkUrlChange);
    return () => window.removeEventListener('focus', checkUrlChange);
  }, [isDirty, currentSurveyId]);

  const handleJumpToNode = useCallback((nodeId) => {
    const targetNode = nodes.find(n => n.id === nodeId || n.data?.id === nodeId);
    if (targetNode) {
      // Use ReactFlow's native selection by setting selected property
      setNodes(prevNodes =>
        prevNodes.map(node => ({
          ...node,
          selected: node.id === targetNode.id
        }))
      );
      setEdges(prevEdges =>
        prevEdges.map(edge => ({ ...edge, selected: false }))
      );
      setSelectedNode(targetNode);
      setSelectedEdge(null);
      try {
        window.dispatchEvent(new CustomEvent('graph-center-node', { detail: { nodeId: targetNode.id } }));
      } catch (_) {}
    }
  }, [nodes]);

  const handleSplitterMouseDown = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleMouseMove = useCallback((e) => {
    // Right splitter
    if (isDragging) {
      const containerWidth = window.innerWidth;
      const newRightPanelWidth = containerWidth - e.clientX;
      const constrainedWidth = Math.min(Math.max(newRightPanelWidth, 300), 800);
      setRightPanelWidth(constrainedWidth);
    }
    // Left splitter
    if (isLeftDragging) {
      const newLeftWidth = e.clientX;
      const constrained = Math.min(Math.max(newLeftWidth, 160), 600);
      setLeftPanelWidth(constrained);
    }
  }, [isDragging, isLeftDragging]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setIsLeftDragging(false);
  }, []);

  // Add global mouse event listeners for dragging
  React.useEffect(() => {
    if (isDragging || isLeftDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isDragging, isLeftDragging, handleMouseMove, handleMouseUp]);

  // Listen for graph node double-click requests to jump and focus editor
  useEffect(() => {
    const handler = (e) => {
      const nodeId = e.detail?.nodeId;
      if (!nodeId) return;
      handleJumpToNode(nodeId);
      // Focus the question text area after panel renders
      setTimeout(() => {
        const el = document.getElementById('question-text');
        if (el && typeof el.focus === 'function') {
          el.focus();
        }
      }, 0);
    };
    window.addEventListener('graph-node-doubleclick', handler);
    return () => window.removeEventListener('graph-node-doubleclick', handler);
  }, [handleJumpToNode]);

  // Persist in-node text edits coming from QuestionNode inline editor
  useEffect(() => {
    const onTextUpdate = (e) => {
      const { nodeId, text } = e.detail || {};
      if (!nodeId) return;
      setNodes(prev => prev.map(n => n.id === nodeId ? { ...n, data: { ...n.data, text } } : n));
      try {
        setSelectedNode(prev => (prev && prev.id === nodeId) ? { ...prev, data: { ...prev.data, text } } : prev);
      } catch (_) { }
    };
    window.addEventListener('graph-node-text-update', onTextUpdate);
    return () => window.removeEventListener('graph-node-text-update', onTextUpdate);
  }, []);

  // Listen for guard banner events and edge selection requests
  useEffect(() => {
    function onWfGuard(e) {
      try {
        const { errors = 0, warnings = 0 } = e?.detail || {};
        setWfGuard({ show: true, errors, warnings });
        setTimeout(() => setWfGuard(prev => ({ ...prev, show: false })), 4000);
      } catch (_) { }
    }
    function onSelectEdge(e) {
      try {
        const { from, to } = e?.detail || {};
        if (!from || !to) return;
        const match = edges.find(ed => ed.source === from && ed.target === to);
        if (!match) return;
        // Select edge and clear node selection
        setEdges(prev => prev.map(ed => ({ ...ed, selected: ed.id === match.id })));
        setNodes(prev => prev.map(n => ({ ...n, selected: false })));
        setSelectedEdge(match);
        setSelectedNode(null);
        // Center to target node
        handleJumpToNode(to);
      } catch (_) { }
    }
    function onGuidedEnd() {
      try {
        // Compute unreachable-to-End nodes reachable from START, then connect to sole End if exactly one
        const startNode = nodes.find(n => n.type === 'start');
        const endNodes = nodes.filter(n => n.type === 'end');
        if (!startNode || endNodes.length !== 1) return;
        const endId = endNodes[0].id;
        // Build adjacency and reverse
        const adj = new Map();
        const rev = new Map();
        edges.forEach(ed => {
          if (!adj.has(ed.source)) adj.set(ed.source, []);
          adj.get(ed.source).push(ed.target);
          if (!rev.has(ed.target)) rev.set(ed.target, []);
          rev.get(ed.target).push(ed.source);
        });
        // Reachable from START
        const reach = new Set();
        const q1 = [startNode.id];
        while (q1.length) { const id = q1.shift(); if (reach.has(id)) continue; reach.add(id); (adj.get(id) || []).forEach(n => { if (!reach.has(n)) q1.push(n); }); }
        // Can reach End (reverse bfs)
        const can = new Set();
        const q2 = [endId];
        while (q2.length) { const id = q2.shift(); if (can.has(id)) continue; can.add(id); (rev.get(id) || []).forEach(p => { if (!can.has(p)) q2.push(p); }); }
        const candidates = Array.from(reach).filter(id => id !== endId && !can.has(id));
        if (!candidates.length) return;
        const ok = window.confirm(`Connect ${candidates.length} node(s) to End? This will add unconditional edges → ${endId}.`);
        if (!ok) return;
        const newEdges = [...edges];
        const ts = Date.now();
        candidates.forEach((fromId, idx) => {
          newEdges.push({ id: `edge-${ts}-${idx}`, source: fromId, target: endId, type: 'default', data: { condition: '', label: '→ End', priority: 1 } });
        });
        setEdges(newEdges);
      } catch (_) { }
    }
    function onAutoFixRequest() {
      try { handleAutoFix(); } catch (_) { }
    }
    function onCleanupLayout() {
      try { handleCleanupLayout(); } catch (_) { }
    }
    function onOpenAppearance() {
      setShowAppearanceModal(true);
    }
    function onUndoRequest() {
      setUndoStack(prev => {
        if (!prev.length) return prev;
        const last = prev[prev.length - 1];
        setNodes(last.nodes);
        setEdges(last.edges);
        if (last.survey) setSurveyData(last.survey);
        setSelectedNode(null);
        setSelectedEdge(null);
        setSelectedNodes([]);
        setSelectedEdges([]);
        setFullValidationResults(null);
        setEdgeValidation(null);
        setIsDirty(true);
        return prev.slice(0, -1);
      });
    }
    window.addEventListener('wf-guard', onWfGuard);
    window.addEventListener('graph-select-edge', onSelectEdge);
    window.addEventListener('wf-guided-end', onGuidedEnd);
    window.addEventListener('wf-autofix-request', onAutoFixRequest);
    window.addEventListener('wf-undo-request', onUndoRequest);
    window.addEventListener('graph-cleanup-layout', onCleanupLayout);
    window.addEventListener('graph-open-appearance', onOpenAppearance);
    return () => {
      window.removeEventListener('wf-guard', onWfGuard);
      window.removeEventListener('graph-select-edge', onSelectEdge);
      window.removeEventListener('wf-guided-end', onGuidedEnd);
      window.removeEventListener('wf-autofix-request', onAutoFixRequest);
      window.removeEventListener('wf-undo-request', onUndoRequest);
      window.removeEventListener('graph-cleanup-layout', onCleanupLayout);
      window.removeEventListener('graph-open-appearance', onOpenAppearance);
    };
  }, [edges, handleJumpToNode, nodes, handleAutoFix, handleCleanupLayout]);

  // Periodically merge runtime variable values into the variable map for display
  useEffect(() => {
    const intervalId = setInterval(() => {
      try {
        // Ensure runtime var store exists even if test controls haven't created it yet
        if (typeof window !== 'undefined') {
          window.surveyEngine = window.surveyEngine || {};
          window.surveyEngine.currentRun = window.surveyEngine.currentRun || { variables: {} };
          window.surveyEngine.currentRun.variables = window.surveyEngine.currentRun.variables || {};
        }
        const runtimeVars = (window && window.surveyEngine && window.surveyEngine.currentRun && window.surveyEngine.currentRun.variables) || {};
        setVariableMap((prev) => {
          if (!prev || prev.size === 0) return prev;
          const next = new Map();
          prev.forEach((rec, name) => {
            const val = Object.prototype.hasOwnProperty.call(runtimeVars, name) ? runtimeVars[name] : rec.actualValue;
            next.set(name, { ...rec, actualValue: val });
          });
          return next;
        });
      } catch (_) {
        // silent per philosophy
      }
    }, 300);
    return () => clearInterval(intervalId);
  }, []);

  // Variable-reference guard: checks if a node defines variables still used
  // elsewhere. Returns true if the delete should proceed, false to cancel.
  // Accepts a single node or a list; a use only counts as surviving if the node
  // using it isn't itself part of the same delete.
  const checkVariableRefsBeforeDelete = useCallback((nodesToDelete) => {
    const doomed = (Array.isArray(nodesToDelete) ? nodesToDelete : [nodesToDelete]).filter(Boolean);
    if (!doomed.length || !variableMap || variableMap.size === 0) return true;
    const doomedIds = new Set(doomed.map(n => n.id));
    const conflicts = [];
    for (const node of doomed) {
      const nodeLabel = node.data?.id || node.id;
      for (const rec of variableMap.values()) {
        if (!rec.definedAt.includes(nodeLabel)) continue;
        const survivingUses = rec.usedAt.filter(usedLabel => {
          const usedNode = nodes.find(n => (n.data?.id || n.id) === usedLabel);
          return usedNode && !doomedIds.has(usedNode.id);
        });
        if (survivingUses.length > 0) {
          conflicts.push({ node: nodeLabel, name: rec.name, usedAt: survivingUses });
        }
      }
    }
    if (conflicts.length === 0) return true;
    const summary = conflicts
      .map(c => `  • ${c.name} (defined by ${c.node}, used by: ${c.usedAt.join(', ')})`)
      .join('\n');
    const subject = doomed.length === 1
      ? `Node "${doomed[0].data?.id || doomed[0].id}" defines variables`
      : `The ${doomed.length} selected nodes define variables`;
    return window.confirm(
      `${subject} still referenced elsewhere:\n${summary}\n\nDelete anyway?`
    );
  }, [variableMap, nodes]);

  // Single path for removing a selection, used by both the Delete key and the
  // toolbar action. Drops incident edges along with the nodes.
  const removeSelection = useCallback(({ confirmFirst }) => {
    const doomedNodes = selectedNodes.length ? selectedNodes : (selectedNode ? [selectedNode] : []);
    const doomedEdges = selectedEdges.length ? selectedEdges : (selectedEdge ? [selectedEdge] : []);
    if (!doomedNodes.length && !doomedEdges.length) return;

    if (doomedNodes.length && !checkVariableRefsBeforeDelete(doomedNodes)) return;

    if (confirmFirst) {
      const parts = [];
      if (doomedNodes.length) parts.push(`${doomedNodes.length} node${doomedNodes.length > 1 ? 's' : ''}`);
      if (doomedEdges.length) parts.push(`${doomedEdges.length} connector${doomedEdges.length > 1 ? 's' : ''}`);
      if (!window.confirm(`Delete ${parts.join(' and ')}?`)) return;
    }

    const nodeIds = new Set(doomedNodes.map(n => n.id));
    const edgeIds = new Set(doomedEdges.map(e => e.id));
    if (nodeIds.size) setNodes(nds => nds.filter(n => !nodeIds.has(n.id)));
    setEdges(eds => eds.filter(e =>
      !edgeIds.has(e.id) && !nodeIds.has(e.source) && !nodeIds.has(e.target)
    ));
    setSelectedNode(null);
    setSelectedEdge(null);
    setSelectedNodes([]);
    setSelectedEdges([]);
  }, [selectedNodes, selectedEdges, selectedNode, selectedEdge, checkVariableRefsBeforeDelete]);

  // Delete key handler for nodes/edges
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ignore when typing in inputs/textarea
      if (['INPUT', 'TEXTAREA'].includes(e.target.tagName)) return;
      if (e.key !== 'Delete' && e.key !== 'Backspace') return;
      removeSelection({ confirmFirst: false });
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [removeSelection]);

  const deleteSelection = useCallback(() => {
    removeSelection({ confirmFirst: true });
  }, [removeSelection]);

  return (
    <NotificationProvider>
      <div className="app">
        <style>{`
          @keyframes pulse {
            0% { transform: scale(1); }
            50% { transform: scale(1.05); }
            100% { transform: scale(1); }
          }
        `}</style>

        {/* Masthead + toolbar band come from the shared shell (§2). Designer's
            existing Toolbar is passed through the transitional `toolbar` slot
            until its controls are regrouped left/centre/right in Designer's own
            remediation phase. */}
        <AppShell
          wordmark="Designer"
          logo={<SamLogo size={36} variant="light" />}
          help={(
            <Button
              variant="onDark"
              onClick={() => openHelpAgent({ context: 'designer', userId: user?.user_id, userName: user?.name || user?.display_name })}
              title="Open Sam Help Agent"
            >
              <HelpCircle size={16} strokeWidth={2.25} /> Help
            </Button>
          )}
          toolbar={<Toolbar
          surveyData={surveyData}
          nodes={nodes}
          edges={edges}
          onSurveyDataChange={handleSurveyDataChange}
          onToggleAssistant={() => setShowAssistant(prev => !prev)}
          selectedNode={selectedNode}
          selectedEdge={selectedEdge}
          selectionCount={selectedNodes.length + selectedEdges.length}
          onDeleteSelection={deleteSelection}
          onLoadSurvey={(loadedData) => {
            setSurveyData(loadedData.survey);
            setNodes(normalizeNodes(loadedData.nodes));
            setEdges(loadedData.edges);
            // Canvas appearance is a user preference — loading a survey
            // (or any canvas key in older payloads) does not touch it.
            setSelectedNode(null);
            setSelectedEdge(null);
            setFullValidationResults(null); // Clear validation on load
            setEdgeValidation(null);
            setIsDirty(false);
          }}
          onValidateSurvey={handleFullValidation}
          initialSurveyContext={initialSurveyContext}
          currentSurveyId={currentSurveyId}
          setCurrentSurveyId={setCurrentSurveyId}
          currentVersionNumber={currentVersionNumber}
          setCurrentVersionNumber={setCurrentVersionNumber}
          onExposeSaveApi={exposeSaveApi}
          onCloudSaved={handleCloudSaved}
        />}
          contentStyle={{ padding: 0, gap: 0, overflow: 'hidden' }}
        >

        <div className="main-content">
          <DraftRecoveryNotice
            draft={recoveredDraft}
            onRestore={restoreDraft}
            onDiscard={discardDraft}
          />
          {wfGuard.show && (
            <div style={{ position: 'fixed', top: 52, left: 12, zIndex: 999, background: '#fff3cd', border: '1px solid #ffeeba', borderRadius: 4, padding: '6px 10px', color: '#856404' }}>
              {`Validation notice: ${wfGuard.errors} error(s)`}{wfGuard.warnings ? ` • ${wfGuard.warnings} warning(s)` : ''}
            </div>
          )}
          <VariablesPanel variableMap={variableMap} onJump={handleJumpToNode} style={{ width: `${leftPanelWidth}px` }} />
          <div className="left-splitter" onMouseDown={() => setIsLeftDragging(true)} />
          <div className="left-panel">
            <ReactFlowProvider>
              <DesignerDerivedProvider nodes={nodes} edges={edges} defaultEdgeType={canvasSettings.defaultEdgeType} edgeFontPx={ptToPx(canvasSettings.edgeTextPt || 10.5)} arrowSize={canvasSettings.arrowSize}>
                <GraphEditor
                  nodes={decoratedNodes}
                  edges={decoratedEdges}
                  onNodesChange={setNodes}
                  onEdgesChange={setEdges}
                  onSelectionChange={handleSelectionChange}
                  errorNodeIds={errorNodeIds}
                  warningNodeIds={warningNodeIds}
                  issueByNode={issueByNode}
                  variableMap={variableMap}
                  canvasSettings={canvasSettings}
                  data-testid="survey-designer"
                />
              </DesignerDerivedProvider>
            </ReactFlowProvider>
            {showAppearanceModal && (
              <CanvasAppearanceModal
                settings={canvasSettings}
                onChange={handleCanvasSettingsChange}
                onApplyConnectorStyleToAll={handleApplyConnectorStyleToAll}
                onClose={() => setShowAppearanceModal(false)}
              />
            )}
            {autoFixPlan && (
              autoFixPlan.items.length > 0 ? (
                <AutoFixModal
                  items={autoFixPlan.items}
                  onApply={handleApplyAutoFixes}
                  onClose={() => setAutoFixPlan(null)}
                />
              ) : (
                <div
                  style={{ position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  onMouseDown={() => setAutoFixPlan(null)}
                >
                  <div style={{ background: '#fff', borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.2)', padding: '18px 22px', maxWidth: 360 }}>
                    <h3 style={{ margin: '0 0 6px', fontSize: 16, fontWeight: 600 }}>Auto-fix</h3>
                    <p style={{ margin: '0 0 14px', fontSize: 14, color: '#5F6368' }}>No fixable issues found.</p>
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <button onClick={() => setAutoFixPlan(null)} style={{ padding: '6px 16px', borderRadius: 4, border: '1px solid #DADCE0', background: '#fff', cursor: 'pointer' }}>Close</button>
                    </div>
                  </div>
                </div>
              )
            )}
          </div>

          <div
            className="panel-splitter"
            onMouseDown={handleSplitterMouseDown}
          />

          <div
            className="right-panel"
            style={{ width: `${rightPanelWidth}px` }}
          >
            {/* Well-formed panel removed */}
            <PredicateEditor
              surveyData={surveyData}
              selectedNode={selectedNode}
              selectedEdge={selectedEdge}
              onSurveyDataChange={handleSurveyDataChange}
              onNodeUpdate={handleNodeUpdate}
              onEdgeUpdate={handleEdgeUpdate}
              onDeleteEdge={deleteEdgeById}
              edgeValidation={edgeValidation}
              fullValidationResults={fullValidationResults}
              nodes={nodes}
              edges={edges}
              onShowNotification={(message, type) => console.log(`${type}: ${message}`)}
              onHighlightNode={handleHighlightNode}
              onJump={handleJumpToNode}
              undockedPanels={undockedPanels}
              onUndock={onUndock}
            />
          </div>
        </div>

        <NotificationContainer />
        <AssistantPanel isOpen={showAssistant} onClose={() => setShowAssistant(false)} />

        {/* Floating tear-off panels */}
        {[...undockedPanels].map((sectionId, idx) => {
          const nodeTitleMap = {
            'node-identity': 'Question',
            'node-personality': 'Personality',
            'node-data-capture': 'Data',
          };
          const surveyWideTitleMap = {
            'survey-settings': 'Survey Settings',
            'flow-validation': 'Flow Validation',
          };
          const initialX = 140 + idx * 30;
          const initialY = 100 + idx * 30;

          if (nodeTitleMap[sectionId]) {
            if (!selectedNode) return null;
            return (
              <FloatingPanel
                key={sectionId}
                panelId={sectionId}
                title={nodeTitleMap[sectionId]}
                onDock={() => onDock(sectionId)}
                initialX={initialX}
                initialY={initialY}
              >
                <TabbedNodeEditor
                  node={selectedNode}
                  onNodeUpdate={handleNodeUpdate}
                  validationErrors={fullValidationResults?.[selectedNode.id]}
                  nodes={nodes}
                  edges={edges}
                  renderOnly={sectionId}
                />
              </FloatingPanel>
            );
          }

          if (surveyWideTitleMap[sectionId]) {
            return (
              <FloatingPanel
                key={sectionId}
                panelId={sectionId}
                title={surveyWideTitleMap[sectionId]}
                onDock={() => onDock(sectionId)}
                initialX={initialX}
                initialY={initialY}
              >
                {sectionId === 'survey-settings' ? (
                  <SurveySettings
                    surveyData={surveyData}
                    onSurveyDataChange={handleSurveyDataChange}
                    nodes={nodes}
                    onJump={handleJumpToNode}
                  />
                ) : (
                  <ValidationPanel
                    edgeValidation={edgeValidation}
                    fullValidationResults={fullValidationResults}
                    nodes={nodes}
                    onJump={handleJumpToNode}
                  />
                )}
              </FloatingPanel>
            );
          }

          if (sectionId === 'edge-editor') {
            if (!selectedEdge) return null;
            return (
              <FloatingPanel
                key={sectionId}
                panelId={sectionId}
                title="Edge Editor"
                onDock={() => onDock(sectionId)}
                initialX={initialX}
                initialY={initialY}
              >
                <EdgeEditor
                  edge={selectedEdge}
                  onEdgeUpdate={handleEdgeUpdate}
                  onEdgeDelete={deleteEdgeById}
                  nodes={nodes}
                  edges={edges}
                />
              </FloatingPanel>
            );
          }

          return null;
        })}
        </AppShell>
      </div>
    </NotificationProvider>
  );
}

export default App;
